import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  Breadcrumbs,
  Link,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Key as KeyIcon,
  School as CourseIcon,
  ContentCopy as CopyIcon,
  Publish as PublishIcon,
  Unpublished as UnpublishIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { adminApi } from '../services/api';

interface Course {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  isPublished: boolean;
  workArea?: { name: string } | null;
  _count?: {
    modules: number;
    enrollments: number;
  };
}

export default function AdminCoursesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [uuidDialogOpen, setUuidDialogOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [newUuid, setNewUuid] = useState('');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Fetch all courses
  const { data: coursesData, isLoading } = useQuery({
    queryKey: ['admin', 'courses'],
    queryFn: async () => {
      const response = await adminApi.getCourses();
      return response.data as Course[];
    },
  });

  // Change UUID mutation
  const changeUuidMutation = useMutation({
    mutationFn: ({ currentId, newId }: { currentId: string; newId: string }) =>
      adminApi.changeCourseUuid(currentId, newId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'courses'] });
      setUuidDialogOpen(false);
      setSelectedCourse(null);
      setNewUuid('');
      setSnackbar({ open: true, message: 'UUID erfolgreich geaendert', severity: 'success' });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Fehler beim Aendern der UUID',
        severity: 'error',
      });
    },
  });

  // Toggle publish mutation
  const togglePublishMutation = useMutation({
    mutationFn: ({ id, isPublished }: { id: string; isPublished: boolean }) =>
      adminApi.updateCourse(id, { isPublished }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'courses'] });
      setSnackbar({
        open: true,
        message: variables.isPublished ? 'Kurs veroeffentlicht' : 'Kurs als Entwurf gespeichert',
        severity: 'success',
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Fehler beim Aktualisieren des Kurses',
        severity: 'error',
      });
    },
  });

  const handleTogglePublish = (course: Course) => {
    togglePublishMutation.mutate({ id: course.id, isPublished: !course.isPublished });
  };

  const courses = coursesData || [];

  // Filter courses based on search
  const filteredCourses = courses.filter(
    (course) =>
      course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.slug.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleOpenUuidDialog = (course: Course) => {
    setSelectedCourse(course);
    setNewUuid('');
    setUuidDialogOpen(true);
  };

  const handleChangeUuid = () => {
    if (selectedCourse && newUuid) {
      changeUuidMutation.mutate({ currentId: selectedCourse.id, newId: newUuid });
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSnackbar({ open: true, message: 'UUID kopiert', severity: 'success' });
  };

  const isValidUuid = (uuid: string) => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(uuid);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component="button" underline="hover" color="inherit" onClick={() => navigate('/admin')}>
          Admin
        </Link>
        <Typography color="text.primary">Kurse</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4">Kurs-Verwaltung</Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Kurse verwalten und UUIDs anpassen
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<BackIcon />} onClick={() => navigate('/admin')}>
          Zurueck
        </Button>
      </Box>

      {/* Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Kurse suchen (Titel, Slug oder UUID)..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          size="small"
        />
      </Paper>

      {/* Info Box */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>UUID anpassen:</strong> Klicken Sie auf das Schluessel-Symbol, um die UUID eines Kurses zu aendern.
          Dies ist nuetzlich, um Kurse mit externen App-IDs aus dem ID-Manager zu synchronisieren.
        </Typography>
      </Alert>

      {/* Courses Table */}
      {filteredCourses.length === 0 ? (
        <Alert severity="info">{searchTerm ? 'Keine Kurse gefunden' : 'Keine Kurse vorhanden'}</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Kurs</TableCell>
                <TableCell>UUID</TableCell>
                <TableCell>Slug</TableCell>
                <TableCell align="center">Module</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Einschreibungen</TableCell>
                <TableCell align="right">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCourses.map((course) => (
                <TableRow key={course.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CourseIcon color="primary" fontSize="small" />
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {course.title}
                        </Typography>
                        {course.workArea && (
                          <Typography variant="caption" color="text.secondary">
                            {course.workArea.name}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.75rem',
                          maxWidth: 200,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {course.id}
                      </Typography>
                      <Tooltip title="UUID kopieren">
                        <IconButton size="small" onClick={() => copyToClipboard(course.id)}>
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {course.slug}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={course._count?.modules || 0} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={course.isPublished ? 'Veroeffentlicht' : 'Entwurf'}
                      size="small"
                      color={course.isPublished ? 'success' : 'default'}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleTogglePublish(course)}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={course._count?.enrollments || 0}
                      size="small"
                      variant="outlined"
                      icon={<PeopleIcon />}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                      <Tooltip title={course.isPublished ? 'Als Entwurf speichern' : 'Veroeffentlichen'}>
                        <IconButton
                          color={course.isPublished ? 'default' : 'success'}
                          onClick={() => handleTogglePublish(course)}
                          disabled={togglePublishMutation.isPending}
                        >
                          {course.isPublished ? <UnpublishIcon /> : <PublishIcon />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Zuweisungen verwalten">
                        <IconButton color="primary" onClick={() => navigate(`/admin/assignments?courseId=${course.id}`)}>
                          <PeopleIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="UUID aendern">
                        <IconButton color="primary" onClick={() => handleOpenUuidDialog(course)}>
                          <KeyIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* UUID Change Dialog */}
      <Dialog open={uuidDialogOpen} onClose={() => setUuidDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>UUID aendern</DialogTitle>
        <DialogContent>
          {selectedCourse && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Kurs: <strong>{selectedCourse.title}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Aktuelle UUID:
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontFamily: 'monospace', bgcolor: 'grey.100', p: 1, borderRadius: 1, mb: 2 }}
              >
                {selectedCourse.id}
              </Typography>

              <TextField
                fullWidth
                label="Neue UUID"
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                value={newUuid}
                onChange={(e) => setNewUuid(e.target.value)}
                error={newUuid.length > 0 && !isValidUuid(newUuid)}
                helperText={
                  newUuid.length > 0 && !isValidUuid(newUuid)
                    ? 'Ungueltige UUID Format'
                    : 'App-ID aus dem ID-Manager eingeben'
                }
                sx={{ mt: 1 }}
              />

              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Achtung:</strong> Das Aendern der UUID aktualisiert alle Referenzen (Module, Einschreibungen,
                  Zertifikate). Diese Aktion kann nicht rueckgaengig gemacht werden.
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUuidDialogOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={handleChangeUuid}
            disabled={!newUuid || !isValidUuid(newUuid) || changeUuidMutation.isPending}
          >
            {changeUuidMutation.isPending ? <CircularProgress size={20} /> : 'UUID aendern'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
